import { Divider } from 'antd';
import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import { Entity } from '../../../types.generated';
import { ANTD_GRAY } from '../../entity/shared/constants';
import { getPlatformName } from '../../entity/shared/utils';
import { capitalizeFirstLetterOnly } from '../../shared/textUtil';
import { useEntityRegistry } from '../../useEntityRegistry';
import ContainerLink from '../../entity/shared/containers/profile/header/PlatformContent/ContainerLink';
import {
    StyledRightOutlined,
    ParentNodesWrapper as ParentContainersWrapper,
    Ellipsis,
    StyledTooltip,
} from '../../entity/shared/containers/profile/header/PlatformContent/ParentNodesView';
import { getParentContainerNames } from '../../entity/shared/containers/profile/header/PlatformContent/PlatformContentView';

const EntityWrapper = styled.div<{ shrinkPadding?: boolean }>`
    border-bottom: 1px solid ${ANTD_GRAY[4]};
    padding: ${(props) => (props.shrinkPadding ? '4px 6px' : '12px 20px')};
`;

const PlatformContent = styled.div<{ removeMargin?: boolean }>`
    display: flex;
    align-items: center;
    margin-bottom: ${(props) => (props.removeMargin ? '0' : '5px')};
`;

const StyledDivider = styled(Divider)`
    margin: 0 5px;
`;

const PlatformLogo = styled.img`
    height: 14px;
    margin-right: 5px;
`;

export const EntityName = styled.span<{ shrinkSize?: boolean }>`
    font-size: 14px;
    font-weight: bold;
`;

interface Props {
    entity: Entity;
    displaySearchResult?: boolean;
}

export default function LineageEntityView({ entity, displaySearchResult }: Props) {
    const entityRegistry = useEntityRegistry();
    const genericProps = entityRegistry.getGenericEntityProperties(entity.type, entity);

    const parentContainersRef = useRef<HTMLDivElement>(null);
    const [areContainersTruncated, setAreContainersTruncated] = useState(false);

    const platformLogoUrl = genericProps?.platform?.properties?.logoUrl;
    const platformName = getPlatformName(genericProps);
    const platformInstanceId = genericProps?.dataPlatformInstance?.instanceId;
    // Extract data_system_name from customProperties with priority over platformInstanceId
    const dataSystemName = genericProps?.customProperties?.find((prop) => prop.key === 'data_system_name')?.value;
    const displayInstanceId = dataSystemName || platformInstanceId;
    const parentContainers = genericProps?.parentContainers?.containers;
    const directParentContainer = parentContainers && parentContainers[0];
    const remainingParentContainers = parentContainers && parentContainers.slice(1, parentContainers.length);

    // Check for truncation
    useEffect(() => {
        if (parentContainersRef && parentContainersRef.current && parentContainers?.length) {
            if (parentContainersRef.current.scrollWidth > parentContainersRef.current.clientWidth) {
                setAreContainersTruncated(true);
            }
        }
    }, [parentContainers]);

    return (
        <EntityWrapper shrinkPadding={displaySearchResult}>
            <PlatformContent className="f-text-content f-color-dark-black-s70" removeMargin={displaySearchResult}>
                {platformLogoUrl && (
                    <PlatformLogo src={platformLogoUrl} alt="platform logo" data-testid="platform-logo" />
                )}
                {/* Platform Name */}
                <span>{platformName}</span>
                {(platformName || displayInstanceId || directParentContainer || parentContainers) && (
                    <StyledDivider type="vertical" data-testid="divider" />
                )}
                {/* Entity Type */}
                <span>
                    {capitalizeFirstLetterOnly(genericProps?.subTypes?.typeNames?.[0]) ||
                        entityRegistry.getEntityName(entity.type)}
                </span>

                {/* Platform Instance ID / Data System Name */}
                {!displayInstanceId && <StyledDivider type="vertical" data-testid="divider" />}
                {displayInstanceId && (
                    <>
                        <StyledDivider type="vertical" data-testid="platform-divider" />
                        <span>{displayInstanceId}</span>
                        {directParentContainer && <StyledRightOutlined data-testid="right-arrow" />}
                    </>
                )}
                {/* Parent Containers */}
                <StyledTooltip
                    title={getParentContainerNames(parentContainers)}
                    overlayStyle={areContainersTruncated ? {} : { display: 'none' }}
                >
                    {areContainersTruncated && <Ellipsis>...</Ellipsis>}
                    <ParentContainersWrapper ref={parentContainersRef}>
                        {remainingParentContainers &&
                            remainingParentContainers.map((container, index) => (
                                <span key={container?.urn}>
                                    <ContainerLink container={container} />
                                    {index > 0 && <StyledRightOutlined data-testid="right-arrow" />}
                                </span>
                            ))}
                    </ParentContainersWrapper>
                </StyledTooltip>
            </PlatformContent>
            <EntityName shrinkSize={displaySearchResult}>
                {entityRegistry.getDisplayName(entity.type, entity)}
            </EntityName>
        </EntityWrapper>
    );
}
